import { chakra } from '@chakra-ui/react';
import { type ReactNode } from 'react';
import { Heading } from '../primitives/Heading';
import { Text } from '../primitives/Text';
import { Breadcrumbs, type Crumb } from '../navigation/Breadcrumbs';
import type { LinkComponent } from '../navigation/types';

const Wrap = chakra('header', {
  base: { display: 'flex', flexDirection: 'column', gap: '3', minWidth: 0 },
});

const Bar = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '4',
    flexWrap: 'wrap',
    minWidth: 0,
  },
});

const TitleCol = chakra('div', {
  base: { display: 'flex', flexDirection: 'column', gap: '1', minWidth: 0, flex: '1 1 20rem' },
});

const Eyebrow = chakra('span', {
  base: {
    fontSize: 'xs',
    fontWeight: 'bold',
    letterSpacing: 'wider',
    textTransform: 'uppercase',
    color: 'brand.fg',
  },
});

const Actions = chakra('div', {
  base: { display: 'flex', alignItems: 'center', gap: '2', flexWrap: 'wrap', flexShrink: 0 },
});

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  /**
   * Small on-brand overline above the title. Opt-in and OFF by default — reserved for
   * brand moments (auth / landing), NOT interior product screens, where a deterministic
   * uppercase kicker on every header reads as generic chrome. Let the title carry hierarchy.
   */
  eyebrow?: ReactNode;
  /** Primary action(s), pinned to the right on desktop, wrapping below on mobile. */
  actions?: ReactNode;
  /** Router link component used for breadcrumb links. */
  linkComponent?: LinkComponent;
}

/**
 * The contextual page header (§ composition — "a contextual page header with the
 * primary action pinned"). Optional breadcrumbs, an on-brand eyebrow, a confident
 * editorial title, a description, and a right-aligned primary-action slot.
 */
const PaneTitleRow = chakra('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '3',
    flexWrap: 'wrap',
    minWidth: 0,
  },
});

export interface PaneHeaderProps {
  /** The pane title (h2). */
  title: ReactNode;
  /** Inline status node shown right after the title (e.g. a status badge). */
  status?: ReactNode;
  description?: ReactNode;
  /** Right-aligned action(s) (e.g. a Cancel button). */
  actions?: ReactNode;
  /** Optional secondary row under the title — metadata, badges, timing. */
  meta?: ReactNode;
}

/**
 * The compact, in-pane header for detail views (Run / Approval). A single, shared header
 * treatment — no eyebrow, no breadcrumbs — so the two detail panes stop hand-rolling their
 * own `<Heading> + <Cluster>`. Title carries the hierarchy; an inline `status` slot sits
 * beside it, `actions` pin right, and `meta` renders a secondary row beneath.
 */
export function PaneHeader({ title, status, description, actions, meta }: PaneHeaderProps) {
  return (
    <Wrap>
      <Bar>
        <TitleCol>
          <PaneTitleRow>
            <Heading level={2} size="lg">
              {title}
            </Heading>
            {status}
          </PaneTitleRow>
          {description && (
            <Text tone="secondary" size="md">
              {description}
            </Text>
          )}
        </TitleCol>
        {actions && <Actions>{actions}</Actions>}
      </Bar>
      {meta}
    </Wrap>
  );
}

export function PageHeader({
  title,
  description,
  breadcrumbs,
  eyebrow,
  actions,
  linkComponent,
}: PageHeaderProps) {
  return (
    <Wrap>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs items={breadcrumbs} linkComponent={linkComponent} />
      )}
      <Bar>
        <TitleCol>
          {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
          <Heading level={1} size="xl">
            {title}
          </Heading>
          {description && (
            <Text tone="secondary" size="md">
              {description}
            </Text>
          )}
        </TitleCol>
        {actions && <Actions>{actions}</Actions>}
      </Bar>
    </Wrap>
  );
}
